import { useCallback } from 'react'

interface QuantityHandlerOptions<TId> {
  /**
   * Function to get the current quantity for an item
   */
  getQuantity: (id: TId) => number

  /**
   * Function to update the quantity (called when quantity > 1)
   */
  onUpdate: (id: TId, newQuantity: number) => void

  /**
   * Function to remove the item (called when confirming removal at quantity = 1)
   */
  onRemove: (id: TId) => void

  /**
   * Custom confirmation message. Defaults to "Remove this item?"
   */
  confirmMessage?: string
}

/**
 * Hook for creating a quantity decrement handler with confirmation at quantity = 1.
 *
 * @example
 * ```tsx
 * const handleDecrement = useQuantityHandler({
 *   getQuantity: (id) => quantities[id] || 0,
 *   onUpdate: (id, qty) => updateMutation.mutate({ id, data: { quantity: qty } }),
 *   onRemove: (id) => removeMutation.mutate(id),
 *   confirmMessage: 'Remove this card from your collection?'
 * })
 * ```
 */
export function useQuantityHandler<TId>(options: QuantityHandlerOptions<TId>) {
  const { getQuantity, onUpdate, onRemove, confirmMessage = 'Remove this item?' } = options

  return useCallback(
    (id: TId) => {
      const currentQty = getQuantity(id)

      if (currentQty > 1) {
        onUpdate(id, currentQty - 1)
      } else if (currentQty === 1) {
        if (confirm(confirmMessage)) {
          onRemove(id)
        }
      }
    },
    [getQuantity, onUpdate, onRemove, confirmMessage],
  )
}
